'use client';

import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  Splitter,
  Table,
  Tooltip,
  IconButton,
  Scroll,
  LegacyDialog,
  Button,
  Field,
  Input,
  Textarea,
} from '@terra/ui-design-system';
import {
  Tick01Icon,
  Copy01Icon,
  Globe02Icon,
  Link01Icon,
  Loading03Icon,
  Mail01Icon,
  Message01Icon,
  PlayIcon,
  Add01Icon,
  ServerStack01Icon,
  Settings02Icon,
  StopIcon,
  Delete02Icon,
} from '@terra/ui-icons';
import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import type {
  ColDef,
  ICellRendererParams,
  CellStyle,
  GridApi,
} from 'ag-grid-community';
import { EmptyState } from '../EmptyState';
import { useChatStore } from '../../store/chatStore';
import {
  useInteractStore,
  type InteractSession,
} from '../../store/interact-store';
import type {
  Interaction,
  DynamicResponseConfig,
} from '@terra/shared-contracts-interact';
import { useLayoutStore } from '../../store/layoutStore';
import { useInteractSSE } from '../../hooks/useInteractSSE';
import { createInteractMention } from '../../utils/interactMentionUtils';
import { toasterApi } from '../../app/providers';
import { useTranslations } from 'next-intl';

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
  }
};

const getProtocolIcon = (protocol: string) => {
  switch (protocol.toLowerCase()) {
    case 'http':
    case 'https':
      return Globe02Icon;
    case 'dns':
      return ServerStack01Icon;
    case 'smtp':
      return Mail01Icon;
    default:
      return Link01Icon;
  }
};

const formatTime = (timestamp: string): string => {
  const date = new Date(timestamp);

  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDate = (timestamp: string): string => {
  const date = new Date(timestamp);

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const parseDynamicResponseHeaders = (
  raw: string,
): Record<string, string> | undefined | null => {
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return null;
  }
};

const buildDynamicResponseConfig = ({
  body,
  status,
  delay,
  headers,
}: {
  body: string;
  status: string;
  delay: string;
  headers?: Record<string, string>;
}): DynamicResponseConfig => ({
  ...(body && { body }),
  ...(status && { status: parseInt(status, 10) }),
  ...(delay && { delay: parseFloat(delay) }),
  ...(headers && { headers }),
});
const ProtocolIcon = ({ protocol }: { protocol: string }) => {
  const Icon = getProtocolIcon(protocol);

  return <Icon size={14} />;
};

export function InteractTab() {
  const tInteract = useTranslations('interactTab');
  const { sendHttpMentionsToChat } = useChatStore();
  const {
    sessions,
    activeSessionId,
    selectedInteractionId,
    isGenerating,
    error,
    generateLink,
    resumeSession,
    listSessions,
    refreshInteractions,
    stopSession,
    deleteSession,
    setActiveSession,
    setSelectedInteraction,
    addInteraction,
    removeInteraction,
    clearInteractions,
    markSessionStopped,
    clearError,
  } = useInteractStore();
  // Use proper selectors for reactive updates
  const activeSession = useInteractStore((state) =>
    state.sessions.find((s) => s.id === state.activeSessionId),
  );
  const selectedInteraction = useInteractStore((state) => {
    const session = state.sessions.find((s) => s.id === state.activeSessionId);

    return session?.interactions.find(
      (i) => i.id === state.selectedInteractionId,
    );
  });
  // Get splitter sizes from layout store
  const interactSplitterSizes = useLayoutStore(
    (state) => state.interactSplitterSizes ?? [50, 50],
  );
  const setInteractSplitterSizes = useLayoutStore(
    (state) => state.setInteractSplitterSizes,
  );
  const setDynamicResponse = useInteractStore(
    (state) => state.setDynamicResponse,
  );
  // Track generation success for button feedback
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [showDynamicResponseForm, setShowDynamicResponseForm] = useState(false);
  const [dynamicResponseBody, setDynamicResponseBody] = useState('');
  const [dynamicResponseStatus, setDynamicResponseStatus] = useState('');
  const [dynamicResponseDelay, setDynamicResponseDelay] = useState('');
  const [dynamicResponseHeaders, setDynamicResponseHeaders] = useState('');
  const [isSettingDynamicResponse, setIsSettingDynamicResponse] =
    useState(false);
  const [sessionsGridApi, setSessionsGridApi] = useState<GridApi | null>(null);
  const handleSessionsGridReady = useCallback((params: { api: GridApi }) => {
    setSessionsGridApi(params.api);
  }, []);
  const prevActiveSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionsGridApi || sessionsGridApi.isDestroyed()) return;
    const prevId = prevActiveSessionIdRef.current;
    const currentId = activeSessionId;

    if (prevId === currentId) return;
    prevActiveSessionIdRef.current = currentId ?? null;
    if (currentId) {
      const currentNode = sessionsGridApi.getRowNode(currentId);

      if (currentNode) {
        currentNode.setSelected(true, true);
      }
    }
  }, [sessionsGridApi, activeSessionId]);
  const handleGenerateLink = useCallback(async () => {
    const result = await generateLink();

    if (result) {
      setGenerationSuccess(true);
      setTimeout(() => setGenerationSuccess(false), 1500);
    }
  }, [generateLink]);
  const hasActiveSessions = useMemo(
    () => sessions.some((s) => s.isActive),
    [sessions],
  );

  useInteractSSE({
    enabled: true,
    onInteractionReceived: useCallback(
      ({
        sessionId,
        interaction,
      }: {
        sessionId: string;
        interaction: Interaction;
      }) => {
        addInteraction(sessionId, interaction);
      },
      [addInteraction],
    ),
    onSessionStarted: useCallback(() => {
      listSessions();
    }, [listSessions]),
    onSessionStopped: useCallback(
      ({ sessionId }: { sessionId: string }) => {
        markSessionStopped(sessionId);
      },
      [markSessionStopped],
    ),
  });
  useEffect(() => {
    if (error) {
      toasterApi.error({
        title: 'Interact Error',
        description: error,
      });
      queueMicrotask(clearError);
    }
  }, [error, clearError]);
  useEffect(() => {
    listSessions();
  }, [listSessions]);
  const [expirationCheckTick, setExpirationCheckTick] = useState(0);

  useEffect(() => {
    if (!hasActiveSessions) {
      return;
    }

    const interval = setInterval(() => {
      setExpirationCheckTick((tick) => tick + 1);
    }, 30000);

    return () => clearInterval(interval);
  }, [hasActiveSessions]);
  const expiredSessions = useMemo(
    () =>
      sessions.filter(
        (session) =>
          session.isActive && new Date(session.expiresAt) < new Date(),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions, expirationCheckTick],
  );

  useEffect(() => {
    for (const session of expiredSessions) {
      markSessionStopped(session.id);
    }
  }, [expiredSessions, markSessionStopped]);
  useEffect(() => {
    if (
      activeSession &&
      activeSession.interactions.length === 1 &&
      !selectedInteractionId
    ) {
      setSelectedInteraction(activeSession.interactions[0].id);
    }
  }, [activeSession, selectedInteractionId, setSelectedInteraction]);
  const handleSendInteractionToChat = useCallback(
    (interaction: Interaction, session: InteractSession) => {
      const mention = createInteractMention({
        interaction,
        sessionId: session.id,
        domain: session.domain,
      });

      sendHttpMentionsToChat({
        mentions: [mention],
      });
    },
    [sendHttpMentionsToChat],
  );
  const handleSessionSelect = useCallback(
    (session: InteractSession) => {
      setActiveSession(session.id);
      setSelectedInteraction(null);
      refreshInteractions(session.id);
    },
    [setActiveSession, setSelectedInteraction, refreshInteractions],
  );
  const handleInteractionSelect = useCallback(
    (interaction: Interaction) => {
      setSelectedInteraction(interaction.id);
    },
    [setSelectedInteraction],
  );
  const handleDeleteSession = useCallback(
    (sessionId: string) => {
      deleteSession(sessionId);
    },
    [deleteSession],
  );
  const handleStopSession = useCallback(
    (sessionId: string) => {
      stopSession(sessionId);
    },
    [stopSession],
  );
  const handleResumeSession = useCallback(
    (sessionId: string) => {
      resumeSession(sessionId);
    },
    [resumeSession],
  );
  const handleSetDynamicResponse = useCallback(async () => {
    if (!activeSession) return;
    const parsedHeaders = parseDynamicResponseHeaders(dynamicResponseHeaders);

    if (parsedHeaders === null) {
      toasterApi.error({
        title: 'Invalid headers',
        description: 'Headers must be valid JSON',
      });

      return;
    }

    const config = buildDynamicResponseConfig({
      body: dynamicResponseBody,
      status: dynamicResponseStatus,
      delay: dynamicResponseDelay,
      headers: parsedHeaders,
    });

    setIsSettingDynamicResponse(true);

    try {
      const dynamicUrl = await setDynamicResponse(activeSession.id, config);

      if (!dynamicUrl) return;
      await copyToClipboard(dynamicUrl);
      toasterApi.success({
        title: 'Dynamic response configured',
        description: 'URL copied to clipboard',
      });
      setShowDynamicResponseForm(false);
    } finally {
      setIsSettingDynamicResponse(false);
    }
  }, [
    activeSession,
    dynamicResponseBody,
    dynamicResponseStatus,
    dynamicResponseDelay,
    dynamicResponseHeaders,
    setDynamicResponse,
  ]);
  const handleClearInteractions = useCallback(
    (sessionId: string) => {
      clearInteractions(sessionId);
    },
    [clearInteractions],
  );
  const handleRemoveInteraction = useCallback(
    (sessionId: string, interactionId: string) => {
      removeInteraction(sessionId, interactionId);
    },
    [removeInteraction],
  );
  // Session columns
  const sessionColumns: ColDef<InteractSession>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Session',
        flex: 1,
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams<InteractSession>) => {
          if (!params.data) return null;

          return (
            <HStack gap="1" alignItems="center">
              <Link01Icon size={12} />
              <Text fontSize="xs" truncate>
                {params.data.name}
              </Text>
              {params.data.source === 'copilot' && (
                <Badge size="sm" variant="emphasis">
                  {tInteract('copilotSource')}
                </Badge>
              )}
            </HStack>
          );
        },
      },
      {
        field: 'domain',
        headerName: 'Domain',
        flex: 2,
        minWidth: 150,
        cellRenderer: (params: ICellRendererParams<InteractSession>) => (
          <Text fontSize="xs" fontFamily="mono" truncate>
            {params.value}
          </Text>
        ),
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 70,
        cellRenderer: (params: ICellRendererParams<InteractSession>) => (
          <Badge variant="emphasis" size="sm">
            {params.value
              ? tInteract('sessionActive')
              : tInteract('sessionStopped')}
          </Badge>
        ),
      },
      {
        field: 'interactions',
        headerName: 'Count',
        width: 60,
        cellRenderer: (params: ICellRendererParams<InteractSession>) => (
          <Badge size="sm" variant="outline">
            {params.data?.interactionCount ?? 0}
          </Badge>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Time',
        width: 80,
        valueFormatter: (params) => formatTime(params.value),
      },
    ],
    [tInteract],
  );
  // Interaction columns
  const interactionColumns: ColDef<Interaction>[] = useMemo(
    () => [
      {
        field: 'protocol',
        headerName: '',
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        suppressSizeToFit: true,
        resizable: false,
        cellRenderer: (params: ICellRendererParams<Interaction>) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="100%"
          >
            <ProtocolIcon protocol={params.value} />
          </Box>
        ),
      },
      {
        field: 'protocol',
        headerName: 'Type',
        width: 60,
        cellRenderer: (params: ICellRendererParams<Interaction>) => (
          <Badge size="sm" variant="outline">
            {params.value?.toUpperCase()}
          </Badge>
        ),
      },
      {
        field: 'uniqueId',
        headerName: 'ID',
        flex: 1,
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams<Interaction>) => (
          <Text fontSize="xs" fontFamily="mono" truncate>
            {params.value}
          </Text>
        ),
      },
      {
        field: 'remoteAddress',
        headerName: 'Remote',
        width: 120,
        cellRenderer: (params: ICellRendererParams<Interaction>) => (
          <Text fontSize="xs" fontFamily="mono">
            {params.value}
          </Text>
        ),
      },
      {
        headerName: 'Details',
        flex: 1,
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams<Interaction>) => {
          if (!params.data) return null;
          const interaction = params.data;

          if (interaction.httpMethod && interaction.httpPath) {
            return (
              <Text fontSize="xs" truncate>
                {interaction.httpMethod} {interaction.httpPath}
              </Text>
            );
          }

          if (interaction.qType) {
            return <Text fontSize="xs">{interaction.qType} query</Text>;
          }

          return null;
        },
      },
      {
        field: 'timestamp',
        headerName: 'Time',
        width: 80,
        valueFormatter: (params) => formatTime(params.value),
      },
    ],
    [],
  );
  // Row style for selected session
  const getSessionRowStyle = useCallback(
    (params: { data?: InteractSession }): CellStyle | undefined => {
      if (params.data?.id === activeSessionId) {
        return { backgroundColor: token('colors.bg.muted') };
      }

      return undefined;
    },
    [activeSessionId],
  );
  // Row style for selected interaction
  const getInteractionRowStyle = useCallback(
    (params: { data?: Interaction }): CellStyle | undefined => {
      if (params.data?.id === selectedInteractionId) {
        return { backgroundColor: token('colors.bg.muted') };
      }

      return undefined;
    },
    [selectedInteractionId],
  );

  // Empty state
  if (sessions.length === 0) {
    return (
      <VStack gap="0" h="full" w="full" alignItems="stretch" overflow="hidden">
        {/* Toolbar */}
        <HStack
          w="full"
          p="1"
          borderBottomWidth="1px"
          borderColor="border.default"
          bg="bg.subtle"
          gap="1"
          alignItems="center"
          flexShrink={0}
        >
          <Badge variant="emphasis" size="sm">
            {tInteract('noSessions')}
          </Badge>

          <Box flex="1" />

          <Tooltip
            title={
              isGenerating
                ? 'Generating...'
                : generationSuccess
                  ? 'Created!'
                  : 'Generate new link'
            }
          >
            <IconButton
              size="xs"
              variant="ghost"
              onClick={handleGenerateLink}
              disabled={isGenerating}
              aria-label="Generate new link"
            >
              {isGenerating ? (
                <Box animation="spin 1s linear infinite" display="flex">
                  <Loading03Icon size={12} />
                </Box>
              ) : generationSuccess ? (
                <Tick01Icon size={12} />
              ) : (
                <Add01Icon size={12} />
              )}
            </IconButton>
          </Tooltip>
        </HStack>

        <EmptyState
          icon={Link01Icon}
          title="No Interact Sessions"
          description="Generate a Terra Link to start capturing OOB interactions"
        />
      </VStack>
    );
  }

  return (
    <VStack gap="0" h="full" w="full" alignItems="stretch" overflow="hidden">
      {/* Toolbar */}
      <HStack
        w="full"
        p="1"
        borderBottomWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
        gap="1"
        alignItems="center"
        flexShrink={0}
      >
        <Badge variant="emphasis" size="sm">
          {sessions.length}
        </Badge>
        <Text fontSize="xs" color="fg.muted">
          {tInteract('sessionsLabel')}
        </Text>

        {activeSession && (
          <>
            <Text fontSize="xs" color="fg.muted" ml="2">
              |
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.muted"
              truncate
              maxW="200px"
            >
              {activeSession.domain}
            </Text>
            <Tooltip title="Copy domain">
              <IconButton
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(activeSession.domain)}
                aria-label="Copy domain"
              >
                <Copy01Icon size={12} />
              </IconButton>
            </Tooltip>
          </>
        )}

        <Box flex="1" />

        {activeSession?.isActive && (
          <HStack gap="1" alignItems="center">
            <Box
              w="6px"
              h="6px"
              borderRadius="full"
              bg="green.9"
              animation="pulse 2s infinite"
            />
            <Text fontSize="xs" color="fg.muted">
              Live
            </Text>
          </HStack>
        )}

        {activeSession && !activeSession.isActive && (
          <Tooltip title="Resume session">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() => handleResumeSession(activeSession.id)}
              disabled={isGenerating}
              aria-label="Resume session"
            >
              <PlayIcon size={12} />
            </IconButton>
          </Tooltip>
        )}

        {activeSession?.isActive && (
          <Tooltip title="Configure dynamic response">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() => setShowDynamicResponseForm(true)}
              aria-label="Configure dynamic response"
            >
              <Settings02Icon size={12} />
            </IconButton>
          </Tooltip>
        )}

        {activeSession?.isActive && (
          <Tooltip title="Stop listener">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() => handleStopSession(activeSession.id)}
              aria-label="Stop listener"
            >
              <StopIcon size={12} />
            </IconButton>
          </Tooltip>
        )}

        {activeSession && (
          <Tooltip title="Delete session">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() => handleDeleteSession(activeSession.id)}
              aria-label="Delete session"
            >
              <Delete02Icon size={12} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip
          title={
            isGenerating
              ? 'Generating...'
              : generationSuccess
                ? 'Created!'
                : 'Generate new link'
          }
        >
          <IconButton
            size="xs"
            variant="ghost"
            onClick={handleGenerateLink}
            disabled={isGenerating}
            aria-label="Generate new link"
          >
            {isGenerating ? (
              <Box animation="spin 1s linear infinite" display="flex">
                <Loading03Icon size={12} />
              </Box>
            ) : generationSuccess ? (
              <Tick01Icon size={12} />
            ) : (
              <Add01Icon size={12} />
            )}
          </IconButton>
        </Tooltip>
      </HStack>

      {/* Dynamic Response Configuration Dialog */}
      <LegacyDialog.Root
        open={showDynamicResponseForm}
        onOpenChange={(details) =>
          !details.open && setShowDynamicResponseForm(false)
        }
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="3" p="3">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>
                  Configure Dynamic Response
                </LegacyDialog.Title>
                <LegacyDialog.Description>
                  Customize the HTTP response returned when the OOB domain is
                  hit
                </LegacyDialog.Description>
              </VStack>

              <HStack gap="2" w="full">
                <Field.Root w="full">
                  <Field.Label fontSize="xs">Status Code</Field.Label>
                  <Input
                    size="xs"
                    type="number"
                    value={dynamicResponseStatus}
                    onChange={(e) => setDynamicResponseStatus(e.target.value)}
                    placeholder="302"
                  />
                </Field.Root>
                <Field.Root w="full">
                  <Field.Label fontSize="xs">Delay (seconds)</Field.Label>
                  <Input
                    size="xs"
                    type="number"
                    value={dynamicResponseDelay}
                    onChange={(e) => setDynamicResponseDelay(e.target.value)}
                    placeholder="0"
                    min={0}
                    max={30}
                  />
                </Field.Root>
              </HStack>

              <Field.Root w="full">
                <Field.Label fontSize="xs">Headers (JSON)</Field.Label>
                <Input
                  size="xs"
                  value={dynamicResponseHeaders}
                  onChange={(e) => setDynamicResponseHeaders(e.target.value)}
                  placeholder='{"Location": "http://169.254.169.254/latest/meta-data/"}'
                />
              </Field.Root>

              <Field.Root w="full">
                <Field.Label fontSize="xs">Response Body</Field.Label>
                <Textarea
                  size="sm"
                  value={dynamicResponseBody}
                  onChange={(e) => setDynamicResponseBody(e.target.value)}
                  placeholder="Custom response body (HTML, XML, text...)"
                />
              </Field.Root>

              {activeSession?.dynamicUrl && (
                <HStack gap="1" w="full" p="2" bg="bg.subtle" borderRadius="sm">
                  <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                    Current URL:
                  </Text>
                  <Text
                    fontSize="xs"
                    fontFamily="mono"
                    color="fg.default"
                    truncate
                    flex="1"
                  >
                    {activeSession.dynamicUrl}
                  </Text>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onClick={() =>
                      copyToClipboard(activeSession.dynamicUrl ?? '')
                    }
                    aria-label="Copy dynamic URL"
                  >
                    <Copy01Icon size={12} />
                  </IconButton>
                </HStack>
              )}

              <HStack gap="2" justify="flex-end" w="full">
                <LegacyDialog.CloseTrigger asChild>
                  <Button variant="outline" size="xs">
                    Cancel
                  </Button>
                </LegacyDialog.CloseTrigger>
                <Button
                  size="xs"
                  onClick={handleSetDynamicResponse}
                  disabled={isSettingDynamicResponse}
                  loading={isSettingDynamicResponse}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      {/* Content */}
      <Box flex="1" overflow="hidden">
        <Splitter.Root
          orientation="vertical"
          h="100%"
          w="full"
          defaultSize={interactSplitterSizes}
          panels={[{ id: 'sessions' }, { id: 'interactions' }]}
          onResizeEnd={({ size }) => {
            if (setInteractSplitterSizes) {
              setInteractSplitterSizes(size as [number, number]);
            }
          }}
          borderWidth="0"
          gap={0}
        >
          {/* Sessions panel */}
          <Splitter.Panel id="sessions" borderWidth="0" h="full" w="full">
            <Box
              h="full"
              w="full"
              overflow="hidden"
              style={{ position: 'relative', zIndex: 1 }}
            >
              <Table
                columnDefs={sessionColumns}
                rowData={sessions}
                getRowId={(params: { data: InteractSession }) => params.data.id}
                rowSelection="single"
                onRowClicked={(event: { data?: InteractSession }) => {
                  if (event.data) handleSessionSelect(event.data);
                }}
                onGridReady={handleSessionsGridReady}
                getRowStyle={getSessionRowStyle}
                rowHeight={32}
                headerHeight={28}
                noBorder={true}
                fontSize={11}
                headerFontSize={10}
                animateRows={false}
                defaultColDef={{
                  sortable: false,
                  filter: false,
                  resizable: true,
                }}
                suppressCellFocus={true}
                hideColumnControls={true}
              />
            </Box>
          </Splitter.Panel>

          <Splitter.ResizeTrigger
            id="sessions:interactions"
            height="0px"
            minHeight="0px"
            bg="transparent"
            position="relative"
            _after={{
              content: '""',
              position: 'absolute',
              inset: '-4px 0 4px 0',
              height: '10px',
              bg: 'transparent',
              cursor: 'row-resize',
            }}
          />

          {/* Interactions panel */}
          <Splitter.Panel
            id="interactions"
            borderWidth="0"
            borderTopWidth="1px"
            borderColor="border.default"
            h="full"
            w="full"
          >
            <VStack
              h="full"
              w="full"
              gap="0"
              alignItems="stretch"
              overflow="hidden"
            >
              {/* Interactions toolbar */}
              <HStack
                w="full"
                px="2"
                py="1"
                borderBottomWidth="1px"
                borderColor="border.default"
                bg="bg.subtle"
                gap="1"
                alignItems="center"
                flexShrink={0}
              >
                <Text fontSize="xs" fontWeight="medium">
                  Interactions
                </Text>
                {activeSession && (
                  <Badge size="sm" variant="outline">
                    {activeSession.interactions.length}
                  </Badge>
                )}
                <Box flex="1" />
                {activeSession && activeSession.interactions.length > 0 && (
                  <Tooltip title="Clear all interactions">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      onClick={() => handleClearInteractions(activeSession.id)}
                      aria-label="Clear all interactions"
                    >
                      <Delete02Icon size={12} />
                    </IconButton>
                  </Tooltip>
                )}
              </HStack>

              {!activeSessionId || !activeSession ? (
                <Box
                  flex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text color="fg.muted" fontSize="xs">
                    Select a session to view interactions
                  </Text>
                </Box>
              ) : activeSession.interactions.length === 0 ? (
                <Box
                  flex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap="2"
                >
                  <Text color="fg.muted" fontSize="xs">
                    Waiting for interactions...
                  </Text>
                  <Text fontSize="xs" fontFamily="mono" color="fg.subtle">
                    {activeSession.domain}
                  </Text>
                </Box>
              ) : (
                <Splitter.Root
                  orientation="horizontal"
                  h="100%"
                  w="full"
                  defaultSize={[60, 40]}
                  panels={[
                    { id: 'interaction-list' },
                    { id: 'interaction-detail' },
                  ]}
                  borderWidth="0"
                  gap={0}
                >
                  <Splitter.Panel
                    id="interaction-list"
                    borderWidth="0"
                    h="full"
                    w="full"
                  >
                    <Box h="full" w="full">
                      <Table
                        columnDefs={interactionColumns}
                        rowData={activeSession.interactions}
                        getRowId={(params: { data: Interaction }) =>
                          params.data.id
                        }
                        rowSelection="single"
                        onRowClicked={(event: { data?: Interaction }) => {
                          if (event.data) handleInteractionSelect(event.data);
                        }}
                        getRowStyle={getInteractionRowStyle}
                        rowHeight={28}
                        headerHeight={24}
                        noBorder={true}
                        fontSize={11}
                        headerFontSize={10}
                        animateRows={false}
                        defaultColDef={{
                          sortable: false,
                          filter: false,
                          resizable: true,
                        }}
                        suppressCellFocus={true}
                        hideColumnControls={true}
                      />
                    </Box>
                  </Splitter.Panel>

                  <Splitter.ResizeTrigger
                    id="interaction-list:interaction-detail"
                    width="0px"
                    minWidth="0px"
                    bg="transparent"
                    position="relative"
                    _after={{
                      content: '""',
                      position: 'absolute',
                      inset: '0 4px 0 -4px',
                      width: '10px',
                      bg: 'transparent',
                      cursor: 'col-resize',
                    }}
                  />

                  <Splitter.Panel
                    id="interaction-detail"
                    borderWidth="0"
                    borderLeftWidth="1px"
                    borderColor="border.default"
                    borderRadius="0"
                    h="full"
                    w="full"
                  >
                    <VStack
                      h="full"
                      w="full"
                      gap="0"
                      alignItems="stretch"
                      overflow="hidden"
                    >
                      {/* Detail header */}
                      <HStack
                        w="full"
                        px="2"
                        py="1"
                        borderBottomWidth="1px"
                        borderColor="border.default"
                        bg="bg.subtle"
                        gap="1"
                        alignItems="center"
                        flexShrink={0}
                      >
                        <Text fontSize="xs" fontWeight="medium">
                          Details
                        </Text>
                        {selectedInteraction && (
                          <>
                            <ProtocolIcon
                              protocol={selectedInteraction.protocol}
                            />
                            <Badge size="sm" variant="outline">
                              {selectedInteraction.protocol.toUpperCase()}
                            </Badge>
                            <Box flex="1" />
                            <Tooltip title="Delete interaction">
                              <IconButton
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  activeSession &&
                                  handleRemoveInteraction(
                                    activeSession.id,
                                    selectedInteraction.id,
                                  )
                                }
                                aria-label="Delete interaction"
                              >
                                <Delete02Icon size={12} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send to Chat">
                              <IconButton
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  activeSession &&
                                  handleSendInteractionToChat(
                                    selectedInteraction,
                                    activeSession,
                                  )
                                }
                                aria-label="Send to Chat"
                              >
                                <Message01Icon size={12} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </HStack>

                      <Box flex="1" w="full" overflow="hidden">
                        {selectedInteraction ? (
                          <Scroll
                            orientation="vertical"
                            showScrollbar={true}
                            h="full"
                          >
                            <Box p="2">
                              <VStack gap="2" alignItems="stretch">
                                <HStack justifyContent="space-between">
                                  <Text fontSize="xs" color="fg.muted">
                                    Unique ID
                                  </Text>
                                  <Text fontSize="xs" fontFamily="mono">
                                    {selectedInteraction.uniqueId}
                                  </Text>
                                </HStack>
                                <HStack justifyContent="space-between">
                                  <Text fontSize="xs" color="fg.muted">
                                    Timestamp
                                  </Text>
                                  <Text fontSize="xs">
                                    {formatDate(selectedInteraction.timestamp)}
                                  </Text>
                                </HStack>
                                <HStack justifyContent="space-between">
                                  <Text fontSize="xs" color="fg.muted">
                                    Remote
                                  </Text>
                                  <Text fontSize="xs" fontFamily="mono">
                                    {selectedInteraction.remoteAddress}
                                  </Text>
                                </HStack>
                                {selectedInteraction.qType && (
                                  <HStack justifyContent="space-between">
                                    <Text fontSize="xs" color="fg.muted">
                                      Query Type
                                    </Text>
                                    <Badge variant="emphasis" size="sm">
                                      {selectedInteraction.qType}
                                    </Badge>
                                  </HStack>
                                )}
                                {selectedInteraction.httpMethod && (
                                  <HStack justifyContent="space-between">
                                    <Text fontSize="xs" color="fg.muted">
                                      HTTP Method
                                    </Text>
                                    <Badge variant="emphasis" size="sm">
                                      {selectedInteraction.httpMethod}
                                    </Badge>
                                  </HStack>
                                )}
                                {selectedInteraction.httpPath && (
                                  <HStack justifyContent="space-between">
                                    <Text fontSize="xs" color="fg.muted">
                                      HTTP Path
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      fontFamily="mono"
                                      truncate
                                      maxW="200px"
                                    >
                                      {selectedInteraction.httpPath}
                                    </Text>
                                  </HStack>
                                )}

                                {selectedInteraction.rawRequest && (
                                  <Box mt="2">
                                    <Text
                                      fontSize="xs"
                                      fontWeight="medium"
                                      mb="1"
                                    >
                                      Raw Request
                                    </Text>
                                    <Box
                                      p="2"
                                      bg="bg.subtle"
                                      borderRadius="sm"
                                      fontFamily="mono"
                                      fontSize="xs"
                                      whiteSpace="pre-wrap"
                                      wordBreak="break-all"
                                      maxH="150px"
                                      overflow="auto"
                                    >
                                      {selectedInteraction.rawRequest}
                                    </Box>
                                  </Box>
                                )}

                                {selectedInteraction.rawResponse && (
                                  <Box mt="2">
                                    <Text
                                      fontSize="xs"
                                      fontWeight="medium"
                                      mb="1"
                                    >
                                      Raw Response
                                    </Text>
                                    <Box
                                      p="2"
                                      bg="bg.subtle"
                                      borderRadius="sm"
                                      fontFamily="mono"
                                      fontSize="xs"
                                      whiteSpace="pre-wrap"
                                      wordBreak="break-all"
                                      maxH="150px"
                                      overflow="auto"
                                    >
                                      {selectedInteraction.rawResponse}
                                    </Box>
                                  </Box>
                                )}
                              </VStack>
                            </Box>
                          </Scroll>
                        ) : (
                          <Box
                            h="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text color="fg.muted" fontSize="xs">
                              Select an interaction to view details
                            </Text>
                          </Box>
                        )}
                      </Box>
                    </VStack>
                  </Splitter.Panel>
                </Splitter.Root>
              )}
            </VStack>
          </Splitter.Panel>
        </Splitter.Root>
      </Box>
    </VStack>
  );
}
